import { Customer, PlanType } from '../../../admin/modules/NewCustomers/reducer'
import { ReferralDetailsProps } from '../../../oppsync/components/CoSell/ReferralDetails/ReferralDetails/ReferralDetails'
import {
  LeadStage,
  ReferralStage,
} from '../../../oppsync/components/CoSell/types'
import { CosellReferralType } from '../../../oppsync/modules/CoSell/actions'
import { useSelector } from '../../../store'
import { PartnerType, PartnershipTypeValue } from '../../modules/partner/action'
import { ProductType } from '../../modules/partner/reducer'
import { CRMTypes } from '../constants'
import { CloudType } from '../../modules/types'
import { getReferralType } from '../helperFunctions'
import { isAdminRole, hasAdminRoles } from '../isAdminRole'
import { Role, UserRoleForTable } from '../roles'
import { useMemo } from 'react'
import { useLocation, useParams } from 'react-router-dom'
import { AccountData } from '../../modules/accountSelection'

/** Redux stores product keys uppercased (OPPSYNC, FLYOUT, CLOUDFAAS). */
type SelectedProductKey = Uppercase<ProductType>
type CloudAccountsByCloud = Partial<Record<CloudType, string>>
type SelectedAccountIdsByProduct = Partial<
  Record<SelectedProductKey, CloudAccountsByCloud>
>

const getFirstCloudAccountId = (
  cloudAccounts: CloudAccountsByCloud
): string | undefined => {
  for (const accountId of Object.values(cloudAccounts)) {
    if (typeof accountId === 'string') {
      return accountId
    }
  }
  return undefined
}

const emptyCustomerForm: Customer = {
  companyName: '',
  quotationLink: '',
  internalNotes: '',
  crmType: null,
  numberOfSeats: 100,
  isInternal: false,
  includeLabraBot: false,
  users: [
    {
      email: '',
      role: 'PARTNER_OWNER' as UserRoleForTable,
    },
  ],
  subscribedProducts: {},
  partnershipType: [],
  plan: 'free_tier',
}

export const useUserType = () => {
  return useSelector<PartnerType>(state => {
    const authRoles: string[] = state.authorization?.roles ?? []
    if (authRoles.length > 0) {
      return hasAdminRoles(authRoles as Role[])
        ? PartnerType.Admin
        : PartnerType.User
    }
    // authorization.roles empty — fall back to userProfile.roles
    return isAdminRole(state.userProfile.userProfile)
      ? PartnerType.Admin
      : PartnerType.User
  })
}

const ADMIN_PARTNER_ID_FROM_URL_PATTERN =
  /\/admin\/partners\/(?!create\b)([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/i

/**
 * Extracts the partner ID from an admin partner detail URL (e.g. /oppsync/admin/partners/:id/...).
 */
export const getAdminPartnerIdFromPathname = (
  pathname: string
): string | undefined => {
  const match = pathname.match(ADMIN_PARTNER_ID_FROM_URL_PATTERN)
  return match?.[1]
}

/**
 * Pure function to get tenant partner ID from state
 * Can be used in both hooks and Redux thunks
 *
 * For regular users:
 * 1. state.tenants.selectedTenantId (multi-tenant support)
 * 2. state.AccountData[userType].partnerData.partnerId (fallback)
 *
 * For admin users viewing a partner in the admin UI:
 * 1. state.AccountData.admin.selectedPartnerId (set when a partner is selected)
 * 2. Partner ID from /admin/partners/:partner_id URL (before Redux updates)
 * 3. state.AccountData.admin.partnerData.partnerId
 * Does not use tenants.selectedTenantId — that reflects the logged-in admin org, not the viewed partner.
 *
 * @param state - Redux state
 * @param userType - User type (Admin or User)
 * @param pathname - Current URL pathname (optional, for admin URL-based detection)
 * @returns The tenant/partner ID
 */
export const getTenantPartnerIdFromState = (
  state: any,
  userType: PartnerType = PartnerType.User,
  pathname: string = ''
): string | undefined => {
  if (userType === PartnerType.Admin) {
    const selectedPartnerId =
      state.AccountData?.admin?.selectedPartnerId?.trim()
    if (selectedPartnerId) {
      return selectedPartnerId
    }

    const partnerIdFromUrl = getAdminPartnerIdFromPathname(pathname)
    if (partnerIdFromUrl) {
      return partnerIdFromUrl
    }

    return state.AccountData?.admin?.partnerData?.partnerId
  }

  const tenantId = state.tenants?.selectedTenantId
  const partnerDataId = state.AccountData[userType]?.partnerData?.partnerId

  return tenantId || partnerDataId
}

/**
 * Returns the tenant/partner ID (the organization's ID, not the selected account ID)
 * Use this when you need the actual tenant ID regardless of selected account
 *
 * For admin users on /admin/partners/:partner_id routes, returns the viewed partner ID.
 */
export const useTenantPartnerId = () => {
  const userType = useUserType()
  const pathname = useSafeLocation()
  return useSelector<string | undefined>(state =>
    getTenantPartnerIdFromState(state, userType, pathname)
  )
}

/**
 * Extracts the product type from the URL pathname
 * Returns undefined if no product is found in the URL
 *
 * Supports both old and new product names:
 * - oppsync / cosell → 'oppsync'
 * - flyout / marketplace → 'flyout'
 * - cloudfaas → 'cloudfaas'
 */
const getProductFromUrl = (pathname: string): ProductType | undefined => {
  const lowerPath = pathname.toLowerCase()

  if (
    lowerPath.includes('/oppsync') ||
    lowerPath.includes('/cosell') ||
    lowerPath.includes('/co-sell')
  )
    return 'oppsync'
  if (lowerPath.includes('/flyout') || lowerPath.includes('/marketplace'))
    return 'flyout'
  if (lowerPath.includes('/cloudfaas')) return 'cloudfaas'

  return undefined
}

/**
 * Extracts the cloud provider from the URL pathname
 * Returns undefined if no cloud is found in the URL
 */
const getCloudFromUrl = (pathname: string): CloudType | undefined => {
  const lowerPath = pathname.toLowerCase()

  if (lowerPath.includes('/cloud/aws') || lowerPath.includes('/aws'))
    return CloudType.AWS
  if (lowerPath.includes('/cloud/azure') || lowerPath.includes('/azure'))
    return CloudType.AZURE
  if (lowerPath.includes('/cloud/gcp') || lowerPath.includes('/gcp'))
    return CloudType.GCP
  if (lowerPath.includes('/cloud/redhat') || lowerPath.includes('/redhat'))
    return CloudType.REDHAT

  return undefined
}

/**
 * Labra cloud account UUID from HubSpot embedded routes:
 * `.../cosell/{aws|azure|gcp}/{accountUuid}/referral/...` or
 * `.../portal/{portalId}/{aws|azure|gcp}/{accountUuid}/referral/...`
 * (Redux account selection may not be hydrated yet; URL is source of truth.)
 */
export const getHubspotCosellAccountIdFromPath = (pathname: string): string => {
  const cosellMatch = pathname.match(
    /\/cosell\/(?:aws|azure|gcp)\/([0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12})(?:\/|$)/i
  )
  if (cosellMatch?.[1]) return cosellMatch[1]

  const portalCloudMatch = pathname.match(
    /\/portal\/[^/]+\/(?:aws|azure|gcp)\/([0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12})\/referral/i
  )
  return portalCloudMatch?.[1] ?? ''
}

/**
 * Safe hook to get location pathname
 * Returns pathname from router if available, otherwise tries window.location
 * Returns empty string if neither available (e.g., in tests)
 */
const useSafeLocation = (): string => {
  try {
    const location = useLocation()
    return location.pathname
  } catch (error) {
    // Not in Router context (e.g., tests)
    // Try window.location if available
    if (typeof window !== 'undefined' && window.location) {
      return window.location.pathname
    }
    // No location available, return empty string
    return ''
  }
}

/**
 * Core logic to get partner ID from state (pure function)
 * Can be used in both hooks and Redux thunks
 *
 * @param state - Redux state
 * @param pathname - Current URL pathname (optional, for URL-based detection)
 * @param userType - User type (Admin or User)
 * @returns The selected account ID for current product, or tenant ID as fallback
 */
export const getPartnerIdFromState = (
  state: any,
  pathname: string = '',
  userType: PartnerType = PartnerType.User
): string | undefined => {
  // Step 1: Get product and cloud from URL pathname
  let productFromUrl = getProductFromUrl(pathname)
  const cloudFromUrl = getCloudFromUrl(pathname)

  // Get selected accounts and tenant ID
  const selectedAccountIds = state.accountSelection?.selectedAccountIds
  const tenantPartnerId = getTenantPartnerIdFromState(state, userType, pathname)

  // Step 1b: No product segment in URL (e.g. embedded HubSpot). Prefer OPPSYNC + AWS
  // account from CloudAccountChangeV2 / SET_SELECTED_ACCOUNT (see accountSelection reducer).
  if (!productFromUrl) {
    const oppsyncAwsId = (
      selectedAccountIds as
        | Record<string, Partial<Record<CloudType, string>>>
        | undefined
    )?.OPPSYNC?.[CloudType.AWS]
    if (oppsyncAwsId) {
      return oppsyncAwsId
    }
  }

  // Step 2: If we have a product, try to get the account ID
  // Convert product to uppercase to match the data structure keys (OPPSYNC, FLYOUT, CLOUDFAAS)
  if (productFromUrl) {
    const productKey = productFromUrl.toUpperCase() as SelectedProductKey
    const cloudAccounts = (
      selectedAccountIds as SelectedAccountIdsByProduct | undefined
    )?.[productKey]

    if (cloudAccounts) {
      // Step 3a: If cloud is specified in URL, use that specific cloud's account
      if (cloudFromUrl && cloudAccounts[cloudFromUrl]) {
        return cloudAccounts[cloudFromUrl]
      }

      // Step 3b: No cloud in URL, use the first available cloud's account (default)
      const firstCloudAccountId = getFirstCloudAccountId(cloudAccounts)

      // Return the account ID if available, otherwise fallback to tenant partnerId
      return firstCloudAccountId || tenantPartnerId
    }
  }

  // Step 4: Fallback to tenant partnerId if:
  // - No product in URL or state (e.g., /settings, /admin, etc.)
  // - No account selected yet for the product
  return tenantPartnerId
}

/**
 * Returns the currently selected account ID based on the active product and cloud
 * This hook can work in two modes:
 *
 * 1. With optional parameters {product, cloud}: Uses those specific values to lookup the account
 *    - First checks URL params for accountId
 *    - If not in URL, looks up the account from availableAccounts by product and cloud
 *
 * 2. Without parameters: Detects product and cloud from URL pathname
 *    - Reads the URL pathname to determine which product and cloud provider are active
 *    - Returns the selected account ID for that combination
 *    - Falls back to tenant ID if no account is selected
 *
 * Note: Works in tests without Router context by falling back to Redux state
 *
 * @param options - Optional parameters {product, cloud}
 * @returns The selected account ID for the current product/cloud, or tenant ID as fallback
 */
export const useAccountId = (options?: {
  product?: ProductType
  cloud?: CloudType
}) => {
  const userType = useUserType()
  const pathname = useSafeLocation()
  const { accountId: accountIdFromParams } = useParams<{ accountId: string }>()

  return useSelector<string | undefined>(state => {
    // Mode 1: If product and cloud parameters are provided, use direct lookup
    if (options?.product && options?.cloud) {
      // First check URL params for accountId
      if (accountIdFromParams) {
        return accountIdFromParams
      }

      // Then lookup by product and cloud from availableAccounts
      const accounts = state.accountSelection.availableAccounts
      const accountData = (Array.isArray(accounts) ? accounts : []).filter(
        (account: AccountData) =>
          account?.product?.toLowerCase() === options.product
      )
      return (
        accountData?.find(
          (account: AccountData) => account?.cloud === options.cloud
        )?.id || ''
      )
    }

    // Mode 2: No parameters provided, use URL-based detection logic
    return getPartnerIdFromState(state, pathname, userType)
  })
}

/**
 * Pure function to get OPPSYNC AWS account ID from state
 * Can be used in both hooks and Redux thunks
 *
 * @param state - Redux state
 * @param userType - User type (Admin or User)
 * @returns The OPPSYNC AWS account ID, or undefined if not found
 */
export const getOppsyncAwsAccountIdFromState = (
  state: any,
  userType: PartnerType = PartnerType.User
): string | undefined => {
  const availableAccounts = state.accountSelection?.availableAccounts || []
  const oppsyncAwsAccount = availableAccounts?.find(
    (account: any) =>
      account?.product?.toUpperCase() === 'OPPSYNC' && account?.cloud === 'AWS'
  )
  return oppsyncAwsAccount?.id
}

/**
 * Pure function to get OPPSYNC AZURE account ID from state
 * Can be used in both hooks and Redux thunks
 *
 * @param state - Redux state
 * @param userType - User type (Admin or User)
 * @returns The OPPSYNC AZURE account ID, or undefined if not found
 */
export const getOppsyncAzureAccountIdFromState = (
  state: any,
  userType: PartnerType = PartnerType.User
): string | undefined => {
  const availableAccounts = state.accountSelection?.availableAccounts || []
  const oppsyncAzureAccount = availableAccounts?.find(
    (account: any) =>
      account?.product?.toUpperCase() === 'OPPSYNC' &&
      account?.cloud === 'AZURE'
  )
  return oppsyncAzureAccount?.id
}

/**
 * Returns the OPPSYNC AWS account ID from availableAccounts (Hook version)
 * This is useful for OPPSYNC-specific AWS operations
 *
 * @returns The OPPSYNC AWS account ID, or undefined if not found
 */
export const useOppsyncAwsAccountId = () => {
  const userType = useUserType()
  return useSelector<string | undefined>(state =>
    getOppsyncAwsAccountIdFromState(state, userType)
  )
}

/**
 * Returns the OPPSYNC AZURE account ID from availableAccounts (Hook version)
 * This is useful for OPPSYNC-specific AZURE operations
 *
 * @returns The OPPSYNC AZURE account ID, or undefined if not found
 */
export const useOppsyncAzureAccountId = () => {
  const userType = useUserType()
  return useSelector<string | undefined>(state =>
    getOppsyncAzureAccountIdFromState(state, userType)
  )
}

export const useCurrentProduct = (defaultProduct: ProductType) => {
  const userType = useUserType()
  return useSelector<ProductType>(
    state => state.AccountData[userType]?.currentProduct || defaultProduct
  )
}

export const customerFormData = () => {
  const userType = useUserType()
  const partnerData = useSelector(
    state => state?.AccountData[userType]?.partnerData
  )

  return useMemo<Customer>(
    () => ({
      companyName: partnerData?.companyName || emptyCustomerForm.companyName,
      quotationLink:
        partnerData?.quotationLink || emptyCustomerForm.quotationLink,
      internalNotes:
        partnerData?.internalNotes || emptyCustomerForm.internalNotes,
      crmType: partnerData?.crmType || emptyCustomerForm.crmType,
      numberOfSeats:
        partnerData?.numberOfSeats || emptyCustomerForm.numberOfSeats,
      isInternal: partnerData?.isInternal || emptyCustomerForm.isInternal,
      includeLabraBot:
        partnerData?.includeLabraBot || emptyCustomerForm.includeLabraBot,
      users: partnerData?.users || emptyCustomerForm.users,
      subscribedProducts:
        partnerData?.subscribedProducts || emptyCustomerForm.subscribedProducts,
      partnershipType:
        (partnerData?.partnershipType as PartnershipTypeValue[]) ||
        emptyCustomerForm.partnershipType,
      plan:
        (partnerData?.plan?.toLowerCase() as PlanType) ||
        emptyCustomerForm.plan,
    }),
    [partnerData]
  )
}
export const getCosellFormData = () => {
  return useSelector<ReferralDetailsProps | undefined>(state => {
    let cosellReferralType: CosellReferralType
    const typeFromOpportunity =
      state.referralFormCreation.referral?.referralMetadata?.referralType
    const typeFromLead =
      state.leadReferralForm.referral?.referralMetadata?.referralType
    if (typeFromOpportunity) {
      cosellReferralType = 'opportunity'
    } else if (typeFromLead) {
      cosellReferralType = 'lead'
    } else {
      // data is not ready so returning undefined
      return undefined
    }
    if (cosellReferralType === 'opportunity') {
      const referral = state.referralFormCreation.referral
      if (!referral || !referral.referralObjectTransformed) {
        // data is not ready so returning undefined
        return undefined
      }
      return {
        referralType: getReferralType(
          'opportunity',
          referral.referralObjectTransformed.referredBy || 'AWS Referral'
        ),
        referralId: referral.referralMetadata.referralId || '',
        referredBy: referral.referralObjectTransformed.referredBy,
        acceptanceStatus: referral.referralObjectTransformed.acceptanceStatus,
        targetCloseDate: referral.referralObjectTransformed?.targetCloseDate,
        acceptByDate: referral.referralObjectTransformed?.acceptByDate,
        lastSyncStatus: referral.referralMetadata?.lastSyncStatus,
        isDraft: referral.referralMetadata.referralStatus === 'draft',
        expectedMonthlyAwsRevenue:
          referral.referralObjectTransformed.expectedMonthlyAwsRevenue + '',
        cloudProviderAcceptanceStatus:
          referral.referralObjectTransformed.cloudProviderAcceptanceStatus,
        stage:
          referral.referralObjectTransformed.stage ||
          ('Prospect' as ReferralStage),
        crmUniqueIdentifier:
          referral.referralObjectTransformed.crmUniqueIdentifier,
        engagementScore: referral.referralObjectTransformed.engagementScore,
        createdAt: referral.referralObjectTransformed.createdDate,
        lastModifiedDate: referral.referralObjectTransformed.lastModifiedDate,
        lastUpdatedBy: referral.referralObjectTransformed.lastUpdatedBy,
      }
    } else {
      const referral = state.leadReferralForm.referral
      return {
        referralType: 'incoming-leads',
        referralId: referral?.referralObjectTransformed?.id || '',
        referredBy: referral?.referralObjectTransformed?.referredBy,
        acceptanceStatus: referral?.referralObjectTransformed?.acceptanceStatus,
        targetCloseDate: referral?.referralObjectTransformed?.labraAcceptByDate,
        lastSyncStatus: referral?.referralMetadata?.lastSyncStatus,
        campaignName: referral?.referralObjectTransformed?.campaignName,
        stage:
          referral?.referralObjectTransformed?.awsLeadStatus ||
          ('Prospect' as LeadStage),
        isDraft: referral?.referralMetadata.referralStatus === 'draft',
        crmUniqueIdentifier:
          referral?.referralObjectTransformed?.partnerCRMLeadId,
      }
    }
  })
}
